import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Plus, Database, Bot, FileStack, CheckCircle2, Trash2 } from "lucide-react";
import AssistantAvatar from "@/components/AssistantAvatar";
import { toast } from "sonner";
import { api } from "@/lib/api";
import ConfirmDelete from "@/components/ConfirmDelete";
import ProjectAnalytics from "@/admin/ProjectAnalytics";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const PROFILES = [
  { v: "generic", l: "Genel" },
  { v: "user_guide", l: "Kullanım Kılavuzu" },
  { v: "regulation", l: "Mevzuat / Yönetmelik" },
  { v: "contract", l: "Sözleşme" },
  { v: "faq", l: "SSS" },
  { v: "article", l: "Makale" },
];
const profileLabel = (v) => PROFILES.find((p) => p.v === v)?.l || v;

const LANGS = [
  { v: "tr", l: "Türkçe" },
  { v: "en", l: "English" },
  { v: "auto", l: "Otomatik / Karışık" },
];
const langLabel = (v) => LANGS.find((l) => l.v === v)?.l || "Otomatik / Karışık";

export default function ProjectPage() {
  const { pid } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [datasets, setDatasets] = useState([]);
  const [assistants, setAssistants] = useState([]);
  const [dsOpen, setDsOpen] = useState(false);
  const [asOpen, setAsOpen] = useState(false);
  const [dsForm, setDsForm] = useState({ name: "", description: "", doc_profile: "generic", language: "auto" });
  const [asForm, setAsForm] = useState({ name: "", description: "" });

  const load = () => {
    api.getProject(pid).then(setProject).catch(() => {});
    api.getDatasets(pid).then(setDatasets).catch(() => {});
    api.getAssistants(pid).then(setAssistants).catch(() => {});
  };
  useEffect(load, [pid]);

  const createDataset = async () => {
    if (!dsForm.name.trim()) return toast.error("Veri seti adı gerekli");
    const d = await api.createDataset(pid, dsForm);
    toast.success("Veri seti oluşturuldu");
    setDsOpen(false);
    setDsForm({ name: "", description: "", doc_profile: "generic", language: "auto" });
    navigate(`/datasets/${d.id}`);
  };

  const createAssistant = async () => {
    if (!asForm.name.trim()) return toast.error("Asistan adı gerekli");
    const a = await api.createAssistant(pid, asForm);
    toast.success("Asistan oluşturuldu");
    setAsOpen(false);
    setAsForm({ name: "", description: "" });
    navigate(`/assistants/${a.id}`);
  };

  const removeDataset = async (id) => {
    try { await api.deleteDataset(id); toast.success("Veri seti silindi"); load(); }
    catch { toast.error("Silinemedi"); }
  };
  const removeAssistant = async (id) => {
    try { await api.deleteAssistant(id); toast.success("Asistan silindi"); load(); }
    catch { toast.error("Silinemedi"); }
  };
  const removeProject = async () => {
    try { await api.deleteProject(pid); toast.success("Proje silindi"); navigate("/"); }
    catch { toast.error("Silinemedi"); }
  };

  return (
    <div className="fade-up">
      <button
        data-testid="back-projects-btn"
        onClick={() => navigate("/")}
        className="mb-6 inline-flex items-center gap-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" /> Projeler
      </button>

      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight">{project?.name || "..."}</h1>
          {project?.description && <p className="mt-2 text-muted-foreground">{project.description}</p>}
        </div>
        {project && (
          <ConfirmDelete
            testId="delete-project-dialog"
            title="Projeyi sil?"
            description={`"${project.name}" ve altındaki tüm veri setleri, dokümanlar ve asistanlar kalıcı olarak silinecek. Bu işlem geri alınamaz.`}
            onConfirm={removeProject}
            trigger={
              <Button data-testid="delete-project-btn" variant="outline" size="sm" className="shrink-0 rounded-full text-red-600 hover:bg-red-50 hover:text-red-700">
                <Trash2 className="mr-1.5 h-4 w-4" /> Projeyi sil
              </Button>
            }
          />
        )}
      </div>

      {/* Datasets */}
      <section className="mt-10">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="flex items-center gap-2 text-lg font-bold">
            <Database className="h-5 w-5" /> Veri Setleri
          </h2>
          <Dialog open={dsOpen} onOpenChange={setDsOpen}>
            <DialogTrigger asChild>
              <Button data-testid="new-dataset-btn" variant="outline" size="sm" className="rounded-full">
                <Plus className="mr-1 h-4 w-4" /> Veri seti
              </Button>
            </DialogTrigger>
            <DialogContent data-testid="new-dataset-dialog">
              <DialogHeader><DialogTitle>Yeni veri seti</DialogTitle></DialogHeader>
              <DialogDescription className="sr-only">Bu projeye yeni bir veri seti ekleyin</DialogDescription>
              <div className="space-y-4 py-2">
                <Input
                  data-testid="dataset-name-input"
                  placeholder="Veri seti adı"
                  value={dsForm.name}
                  onChange={(e) => setDsForm({ ...dsForm, name: e.target.value })}
                />
                <Textarea
                  placeholder="Açıklama (opsiyonel)"
                  value={dsForm.description}
                  onChange={(e) => setDsForm({ ...dsForm, description: e.target.value })}
                />
                <div>
                  <label className="mb-1.5 block text-sm font-medium">Doküman profili</label>
                  <Select value={dsForm.doc_profile} onValueChange={(v) => setDsForm({ ...dsForm, doc_profile: v })}>
                    <SelectTrigger data-testid="dataset-profile-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {PROFILES.map((p) => <SelectItem key={p.v} value={p.v}>{p.l}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="mb-1.5 block text-sm font-medium">İçerik dili</label>
                  <Select value={dsForm.language} onValueChange={(v) => setDsForm({ ...dsForm, language: v })}>
                    <SelectTrigger data-testid="dataset-language-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {LANGS.map((p) => <SelectItem key={p.v} value={p.v}>{p.l}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <p className="mt-1.5 text-xs text-muted-foreground">
                    Belgelerin dili. Sohbet sırasında sorunun diline uyan veri setleri önceliklendirilir.
                  </p>
                </div>
              </div>
              <DialogFooter>
                <Button data-testid="dataset-save-btn" onClick={createDataset}>Oluştur</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        {datasets.length === 0 ? (
          <div className="soft-card px-6 py-12 text-center text-sm text-muted-foreground">
            Henüz veri seti yok. Doküman yüklemek için bir veri seti oluşturun.
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {datasets.map((d) => (
              <div
                key={d.id}
                data-testid={`dataset-card-${d.id}`}
                onClick={() => navigate(`/datasets/${d.id}`)}
                className="soft-card soft-card-hover group relative cursor-pointer p-5 text-left"
              >
                <div className="flex items-center justify-between">
                  <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-secondary">
                    <FileStack className="h-4.5 w-4.5 text-foreground" />
                  </div>
                  <div className="flex items-center gap-1.5">
                    <Badge variant="outline" className="rounded-full text-[11px]">{profileLabel(d.doc_profile)}</Badge>
                    <Badge variant="outline" className="rounded-full text-[11px] uppercase">{d.language === "en" ? "EN" : d.language === "tr" ? "TR" : "AUTO"}</Badge>
                    <ConfirmDelete
                      testId={`delete-dataset-dialog-${d.id}`}
                      title="Veri setini sil?"
                      description={`"${d.name}" veri seti, tüm dokümanları ve vektörleri kalıcı olarak silinecek.`}
                      onConfirm={() => removeDataset(d.id)}
                      trigger={
                        <button
                          data-testid={`delete-dataset-${d.id}`}
                          onClick={(e) => e.stopPropagation()}
                          className="rounded-lg p-1 text-muted-foreground opacity-0 transition-opacity hover:bg-red-50 hover:text-red-600 group-hover:opacity-100"
                          title="Veri setini sil"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      }
                    />
                  </div>
                </div>
                <h3 className="mt-3 font-bold">{d.name}</h3>
                <div className="mt-3 flex items-center gap-3 text-xs text-muted-foreground">
                  <span>{d.stats?.documents || 0} doküman</span>
                  <span className="flex items-center gap-1"><CheckCircle2 className="h-3 w-3 text-emerald-600" />{d.stats?.ready || 0} hazır</span>
                  <span>{d.stats?.segments || 0} parça</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Assistants */}
      <section className="mt-12">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="flex items-center gap-2 text-lg font-bold">
            <Bot className="h-5 w-5" /> Asistanlar
          </h2>
          <Dialog open={asOpen} onOpenChange={setAsOpen}>
            <DialogTrigger asChild>
              <Button data-testid="new-assistant-btn" variant="outline" size="sm" className="rounded-full">
                <Plus className="mr-1 h-4 w-4" /> Asistan
              </Button>
            </DialogTrigger>
            <DialogContent data-testid="new-assistant-dialog">
              <DialogHeader><DialogTitle>Yeni asistan</DialogTitle></DialogHeader>
              <DialogDescription className="sr-only">Bu projede yeni bir asistan oluşturun</DialogDescription>
              <div className="space-y-4 py-2">
                <Input
                  data-testid="assistant-name-input"
                  placeholder="Asistan adı"
                  value={asForm.name}
                  onChange={(e) => setAsForm({ ...asForm, name: e.target.value })}
                />
                <Textarea
                  placeholder="Açıklama (opsiyonel)"
                  value={asForm.description}
                  onChange={(e) => setAsForm({ ...asForm, description: e.target.value })}
                />
              </div>
              <DialogFooter>
                <Button data-testid="assistant-save-btn" onClick={createAssistant}>Oluştur</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        {assistants.length === 0 ? (
          <div className="soft-card px-6 py-12 text-center text-sm text-muted-foreground">
            Henüz asistan yok. Veri setlerinize bağlı bir asistan oluşturun.
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {assistants.map((a) => (
              <div
                key={a.id}
                data-testid={`assistant-card-${a.id}`}
                onClick={() => navigate(`/assistants/${a.id}`)}
                className="soft-card soft-card-hover group relative cursor-pointer p-5 text-left"
              >
                <div className="flex items-center justify-between">
                  <AssistantAvatar
                    icon={a.config_draft?.behavior?.icon}
                    color={a.config_draft?.behavior?.widget_color || "#0a0a0a"}
                    size={36}
                  />
                  <div className="flex items-center gap-1.5">
                    <Badge
                      variant="outline"
                      className={a.status === "published"
                        ? "rounded-full bg-emerald-50 text-emerald-700 border-emerald-200"
                        : "rounded-full"}
                    >
                      {a.status === "published" ? "Yayında" : "Taslak"}
                    </Badge>
                    <ConfirmDelete
                      testId={`delete-assistant-dialog-${a.id}`}
                      title="Asistanı sil?"
                      description={`"${a.name}" asistanı, yayınları ve API anahtarları kalıcı olarak silinecek. Veri setleri silinmez.`}
                      onConfirm={() => removeAssistant(a.id)}
                      trigger={
                        <button
                          data-testid={`delete-assistant-${a.id}`}
                          onClick={(e) => e.stopPropagation()}
                          className="rounded-lg p-1 text-muted-foreground opacity-0 transition-opacity hover:bg-red-50 hover:text-red-600 group-hover:opacity-100"
                          title="Asistanı sil"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      }
                    />
                  </div>
                </div>
                <h3 className="mt-3 font-bold">{a.name}</h3>
                <p className="mt-1 text-xs text-muted-foreground">
                  {a.dataset_ids?.length || 0} veri seti bağlı
                </p>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Analytics */}
      <ProjectAnalytics pid={pid} />
    </div>
  );
}
